import { createSelector } from '@reduxjs/toolkit';

import type { CourseActivity, CourseDetail, CourseSectionDetail } from '@/lib/api';
import type { RootState } from '@/state/store';

import type {
    CourseActivityProgress,
    CourseActivityWithProgress,
    CoursePlayerState,
    CourseSectionWithProgress,
} from './types';

export const getCoursePlayerState = (state: RootState): CoursePlayerState => state.coursePlayer;

export const getCourseEntity = createSelector(
    getCoursePlayerState,
    (state: CoursePlayerState): CourseDetail | undefined => state.course
);

/**
 * Get the course progress for each activity
 */
export const getActivityProgress = createSelector(
    getCourseEntity,
    (course: CourseDetail | undefined): CourseActivityProgress => {
        if (!course) {
            return {};
        }

        const { activity_positions = {} } = course;

        return activity_positions;
    }
);

export const getSections = createSelector(
    getCourseEntity,
    getActivityProgress,
    (course, activityProgress): CourseSectionWithProgress[] => {
        const sections = course?.sections ?? [];

        const sectionsWithProgress = sections.map((section) => {
            const activityIds = section.activities?.map((activity) => activity.id) ?? [];
            const activityProgressIds = Object.keys(activityProgress ?? {});

            // A section is visited if all of its activities are visited. A section with no activities is never complete
            const visited =
                activityIds.filter((activityId) => activityProgressIds.includes(activityId)).length ===
                    activityIds.length && activityIds.length !== 0;

            const activities = section.activities?.map((activity) => {
                return {
                    ...activity,
                    visited: activityProgressIds.includes(activity.id),
                    videoTimeSeconds: activityProgress[activity.id]?.video_time_seconds ?? 0,
                };
            });
            return {
                ...section,
                activities,
                visited,
            };
        });
        return sectionsWithProgress;
    }
);

export const getActiveSection = createSelector(
    getCoursePlayerState,
    (state: CoursePlayerState): CourseSectionDetail | undefined => state.activeSection
);

const getActiveSectionIndex = createSelector(
    getCourseEntity,
    getActiveSection,
    (course, getActiveSection): number | undefined => {
        if (!getActiveSection || !course) {
            return undefined;
        }
        const { sections = [] } = course;
        return sections.findIndex((section) => section.id === getActiveSection.id);
    }
);

export const getActivities = createSelector(
    getActiveSection,
    getActivityProgress,
    (section, activityProgress): CourseActivityWithProgress[] => {
        const activities = section?.activities ?? [];

        const sectionsWithProgress = activities.map((activity: CourseActivity): CourseActivityWithProgress => {
            const progress = activityProgress[activity.id] ?? { video_time_seconds: 0, visited: false };
            const { video_time_seconds, visited = false } = progress;

            return {
                ...activity,
                videoTimeSeconds: video_time_seconds ?? 0,
                visited,
            };
        });

        return sectionsWithProgress;
    }
);

export const getActiveActivity = createSelector(
    getCoursePlayerState,
    getActivityProgress,
    (state: CoursePlayerState, progress: CourseActivityProgress): CourseActivityWithProgress | undefined => {
        if (!state.activeActivity) {
            return undefined;
        }
        const activityProgress = progress[state.activeActivity.id] ?? { video_time_seconds: 0, visited: false };
        const { video_time_seconds, visited } = activityProgress;
        return {
            ...state.activeActivity,
            videoTimeSeconds: video_time_seconds ?? 0,
            visited,
        };
    }
);

export const getNextActivity = createSelector(
    getSections,
    getActivities,
    getActiveSectionIndex,
    getActiveActivity,
    (
        sections: CourseSectionDetail[],
        activities: CourseActivity[],
        sectionIndex?: number,
        activity?: CourseActivity
    ): CourseActivity | undefined => {
        if (!activity || sectionIndex === undefined || activities.length === 0) {
            return undefined;
        }

        const { course_section_order } = activity;
        const index = course_section_order - 1;

        // Base case, if there are more activities in the section, return the next one
        if (index !== activities.length - 1) {
            return activities[index + 1];
        }

        // If this was the last section, there is no next activity
        if (sectionIndex === sections.length - 1) {
            return undefined;
        }

        // Return the first activity of the next section
        const nextSection = sections[sectionIndex + 1];
        return nextSection.activities?.[0];
    }
);

export const getPreviousActivity = createSelector(
    getSections,
    getActivities,
    getActiveSectionIndex,
    getActiveActivity,
    (
        sections: CourseSectionDetail[],
        activities: CourseActivity[],
        sectionIndex?: number,
        activity?: CourseActivity
    ): CourseActivity | undefined => {
        if (!activity || sectionIndex === undefined || activities.length === 0) {
            return undefined;
        }

        const { course_section_order } = activity;
        const index = course_section_order - 1;

        // Base case, if there are previous activities in the section, return the next one
        if (index !== 0) {
            return activities[index - 1];
        }

        // If this was the first section, there is no previous activity
        if (sectionIndex === 0) {
            return undefined;
        }

        // Return the last activity of the previous section
        const previousSection = sections[sectionIndex - 1];
        const previousActivities = previousSection.activities ?? [];
        return previousActivities.at(-1);
    }
);

export const getProgress = createSelector(getCoursePlayerState, (state: CoursePlayerState): number => state.progress);

export const getIsCompletable = createSelector(getSections, getNextActivity, (sections, nextActivity): boolean => {
    if (nextActivity !== undefined) {
        return false;
    }
    const requiredSections = sections.filter((s) => s.required_for_completion);
    return requiredSections.length > 0 && requiredSections.every((s) => s.visited);
});
